import { translateDesktopError } from '@/tauri/errors';
import { extractPluginError } from '@heidi/plugin-core';

export { extractPluginError } from '@heidi/plugin-core';

/**
 * Error surfaced by a GDT IPC wrapper.
 *
 * The human-readable message is translated from an allowlisted error code and
 * is safe for local UI state. Remote telemetry must use `errorCode`, which is
 * preserved separately from the Rust plugin response.
 */
export class GdtApiCommandError extends Error {
  constructor(
    message: string,
    readonly errorCode?: string
  ) {
    super(message);
    this.name = 'GdtApiCommandError';
  }
}

/**
 * Extract the machine-readable error code for a GDT command failure, so
 * callers can classify or log a failure without touching its message.
 */
export function getGdtCommandErrorCode(error: unknown): string | undefined {
  return error instanceof GdtApiCommandError ? error.errorCode : undefined;
}

export function createGdtApiCommandError(
  prefix: string,
  error: unknown
): GdtApiCommandError {
  const { errorCode } = extractPluginError(error);
  // Never retain the raw plugin message here. Rust error strings can contain
  // local paths or clinical values, and Error objects may be captured by a
  // remote logger later. Known codes still resolve to a localised UI message.
  return new GdtApiCommandError(translateDesktopError(prefix, errorCode), errorCode);
}

/**
 * Command prefix for invoking GDT plugin commands via Tauri IPC.
 */
export const GDT_COMMAND_PREFIX = 'plugin:gdt|';

/**
 * Format a Tauri plugin error into a display string.
 *
 * If the error is a plugin error object `{ message, error_code }` and has
 * an i18n mapping, returns the translated message. Otherwise falls back
 * to the raw error message.
 */
export function formatError(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const obj = error as { message: string; error_code?: string };
    if (obj.error_code) {
      return translateDesktopError(obj.message, obj.error_code);
    }
    return obj.message;
  }
  return String(error);
}
